import {
  Box,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";

import Header from "../../components/Header.jsx";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import useGraphQLClient from "../../hooks/useGraphQLClient.js";

// Graph
import { CREATE_TENANT_USER } from "../../graph/tenantUsers/mutations.js";
import { READ_ALL_TENANTS } from "../../graph/tenants/queries.js";

// Components
import SingleSelect from "../../components/SingleSelect.jsx";

const CreateTenantUserForm = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const client = useGraphQLClient();
  const { setSnackbar } = useSnackbar();

  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      const cleanedValues = { ...values };

      for (const key of Object.keys(cleanedValues)) {
        if (cleanedValues[key] === "") cleanedValues[key] = null;
      }

      await client.request(CREATE_TENANT_USER, {
        input: cleanedValues,
      });

      setSnackbar({
        open: true,
        message: "Tenant user created successfully!",
        severity: "success",
      });

      resetForm();
      navigate("/system/tenantUsers");
    } catch (err) {
      const message = err?.response?.errors?.[0]?.message || "Unexpected error";
      console.error("Error creating tenant user:", err);

      setSnackbar({
        open: true,
        message: `Failed to create tenant user: ${message}`,
        severity: "error",
      });
    }
  };

  return (
    <Box>
      <Header title="CREATE TENANT USER" subtitle="Create a New Tenant User" />

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialTenantUserValues}
        validationSchema={tenantUserSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              <SingleSelect
                label="Tenant"
                name="tenantId"
                value={values.tenantId}
                query={READ_ALL_TENANTS}
                dataKey="readAllTenants"
                valueKey="id"
                labelKey="name"
                onChange={setFieldValue}
                onBlur={handleBlur}
                error={errors.tenantId}
                touched={touched.tenantId}
              />

              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="User ID"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.userId}
                name="userId"
                error={!!touched.userId && !!errors.userId}
                helperText={touched.userId && errors.userId}
                sx={{ gridColumn: "span 2" }}
              />

              <FormControl
                fullWidth
                variant="filled"
                sx={{ gridColumn: "span 2" }}
              >
                <InputLabel>Access Role</InputLabel>
                <Select
                  name="accessRole"
                  value={values.accessRole}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <MenuItem value="CLIENT_ADMIN">Client Admin</MenuItem>
                  <MenuItem value="CLIENT_STAFF">Client Staff</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <Button type="submit" color="secondary" variant="contained">
                Create Tenant User
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

const tenantUserSchema = yup.object().shape({
  tenantId: yup.string().required("Tenant is required"),
  userId: yup.string().required("User ID is required"),
  accessRole: yup.string().required("Access role is required"),
});

const initialTenantUserValues = {
  tenantId: "",
  userId: "",
  accessRole: "CLIENT_STAFF",
};

export default CreateTenantUserForm;